import { useTheme } from '@mui/material';
import React, { AriaAttributes, forwardRef, ReactElement, useMemo } from 'react';

import type { SxProps, Theme } from '../../../themes/themes';
import { Grid, GridProps } from '../../Grid/Grid';
import { Typography } from '../../Typography/Typography';
import { getStyles } from './Link.styles';

interface LinksProps extends GridProps {
  icon?: ReactElement | null;
  underlined?: boolean;
  component?: 'a' | 'button' | 'div';
  href?: HTMLAnchorElement['href'];
  target?: HTMLAnchorElement['target'];
  rel?: HTMLAnchorElement['rel'];
  'aria-current'?: AriaAttributes['aria-current'];
  sx?: SxProps;
}

export const Link = forwardRef<HTMLDivElement, LinksProps>(
  ({ component = 'a', icon, underlined = false, children, 'aria-current': ariaCurrent, ...props }, ref) => {
    const theme = useTheme<Theme>();
    const styles = getStyles(theme, underlined || ariaCurrent === 'page');
    const sx: GridProps['sx'] = useMemo(
      () => (Array.isArray(props.sx) ? [...props.sx, styles.link] : [props.sx, styles.link]),
      [props.sx, styles.link]
    );

    return (
      <Grid container ref={ref} component={component} {...props} sx={sx}>
        <Typography variant="button" sx={styles.inner}>
          {children}
        </Typography>
        {icon && <Grid>{React.cloneElement(icon, { fontSize: 'inherit', sx: styles.icon })}</Grid>}
      </Grid>
    );
  }
);

Link.displayName = 'NavigationLink';
